if (!customElements.get('infinite-product-grid')) {
  class InfiniteProductGrid extends HTMLElement {
    constructor() {
      super();

      this.records = new Map();
      this.currentPage = 1;
      this.lastDirection = 'forward';
      this.loading = false;
      this.queuedRequest = null;
      this.abortController = null;
      this.scrollFrame = null;
      this.persistTimer = null;
      this.hasUserScrolled = false;
      this.restoringScroll = false;
      this.scrollPersistenceEnabled = false;
      this.restoreFrame = null;
      this.restoreToken = 0;
      this.resolveScrollRestoration = null;
      this.headerRestorationActive = false;

      this.onIntersection = this.onIntersection.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.onBackToTopClick = this.onBackToTopClick.bind(this);
      this.onPageHide = this.onPageHide.bind(this);
      this.onVisibilityChange = this.onVisibilityChange.bind(this);
      this.onGridClick = this.onGridClick.bind(this);
      this.cancelScrollRestoration = this.cancelScrollRestoration.bind(this);
    }

    connectedCallback() {
      if (this.initialized || !window.IntersectionObserver || !window.fetch || !window.DOMParser) return;

      this.grid = this.querySelector(this.dataset.gridSelector || '#product-grid');
      if (!this.grid) return;

      this.initialized = true;
      this.clientControlled = this.dataset.clientControlled === 'true';
      this.scrollPersistenceEnabled = false;
      this.clientHasNext = this.dataset.clientHasNext !== 'false';
      this.prefetchMargin = navigator.connection?.saveData ? 240 : 900;
      this.sectionId = this.dataset.sectionId;
      this.pageParam = this.dataset.pageParam || 'page';
      this.maxLiveMedia = Number.parseInt(this.dataset.maxLiveMedia, 10) || 48;
      this.currentPage = Number.parseInt(this.dataset.currentPage, 10) || 1;
      this.status = this.querySelector('[data-infinite-status]');
      this.statusText = this.querySelector('[data-infinite-status-text]');
      this.pagination = this.querySelector('[data-infinite-pagination]');
      this.backToTop = this.querySelector('[data-infinite-back-to-top]');
      this.backToTopVisible = false;
      this.lastScrollY = window.scrollY;
      this.previousScrollY = this.lastScrollY;

      if (this.backToTop) {
        this.backToTopAnchor = document.createComment('Infinite product grid back-to-top button');
        this.backToTop.before(this.backToTopAnchor);
        document.body.append(this.backToTop);
      }

      this.createInitialRecord();
      if (!this.clientControlled && this.records.get(this.currentPage)) {
        this.replaceCurrentHistory(this.records.get(this.currentPage));
      }

      this.snapshot = this.hasScrollRestoreIntent() ? this.readSnapshot() : null;
      if (!this.snapshot) this.clearScrollSnapshot();
      this.observer = new IntersectionObserver(this.onIntersection, {
        rootMargin: `${this.prefetchMargin}px 0px`,
      });
      this.enforceMediaBudget();
      this.observeAllRecords();

      if (this.pagination) this.pagination.hidden = true;
      this.classList.add('is-enhanced');
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('pagehide', this.onPageHide);
      document.addEventListener('visibilitychange', this.onVisibilityChange);
      window.addEventListener('pointerdown', this.cancelScrollRestoration, { passive: true });
      window.addEventListener('touchstart', this.cancelScrollRestoration, { passive: true });
      this.backToTop?.addEventListener('click', this.onBackToTopClick);
      this.grid.addEventListener('click', this.onGridClick);
      if (!this.clientControlled) {
        this.restoreSavedSnapshot(this.snapshot)
          .catch(() => this.clearScrollSnapshot())
          .finally(() => {
            this.clearScrollSnapshot();
            this.enableScrollPersistence();
          });
      }
      this.scheduleSync();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.observer?.disconnect();
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('pagehide', this.onPageHide);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      window.removeEventListener('pointerdown', this.cancelScrollRestoration);
      window.removeEventListener('touchstart', this.cancelScrollRestoration);
      this.backToTop?.removeEventListener('click', this.onBackToTopClick);
      this.grid?.removeEventListener('click', this.onGridClick);
      this.backToTopAnchor?.replaceWith(this.backToTop);
      this.backToTopAnchor = null;
      if (this.scrollFrame) window.cancelAnimationFrame(this.scrollFrame);
      if (this.persistTimer) window.clearTimeout(this.persistTimer);
      this.cancelScrollRestoration();
      this.initialized = false;
    }

    createInitialRecord() {
      const slots = this.directGridItems();
      if (!slots.length) return;
      slots.forEach((slot, index) => this.markSlot(slot, this.currentPage, index + 1));

      this.records.set(
        this.currentPage,
        this.createRecord({
          page: this.currentPage,
          url: this.currentDocumentUrl(),
          previousUrl: this.dataset.previousUrl,
          nextUrl: this.dataset.nextUrl,
          slots,
        })
      );
    }

    createRecord({ page, url, previousUrl, nextUrl, slots }) {
      return {
        page,
        url: this.canonicalUrl(url),
        previousUrl: previousUrl ? this.canonicalUrl(previousUrl) : null,
        nextUrl: nextUrl ? this.canonicalUrl(nextUrl) : null,
        slots,
      };
    }

    directGridItems() {
      return Array.from(this.grid.children).filter((item) => item.matches('.grid__item'));
    }

    resetClientRecords() {
      if (!this.clientControlled) return;

      this.observer?.disconnect();
      this.records.clear();
      this.currentPage = 1;
      this.dataset.currentPage = '1';
      this.createInitialRecord();
      this.enforceMediaBudget();
      this.observeAllRecords();
      this.scheduleSync();
    }

    beginClientLoad(direction = 'next') {
      const hasMore = direction === 'initial' || this.clientHasNext;
      if (!this.clientControlled || this.loading || !hasMore) return false;

      this.loading = true;
      this.setLoadingState(true);
      return true;
    }

    completeClientLoad() {
      if (!this.clientControlled) return;

      this.loading = false;
      this.setLoadingState(false);
    }

    setClientHasNext(hasNext) {
      if (!this.clientControlled) return;
      this.clientHasNext = Boolean(hasNext);
    }

    enableScrollPersistence() {
      this.scrollPersistenceEnabled = true;
      this.scheduleSnapshot();
    }

    onGridClick(event) {
      if (
        event.defaultPrevented
        || event.button !== 0
        || event.metaKey
        || event.ctrlKey
        || event.shiftKey
        || event.altKey
      ) return;

      const link = event.target.closest('a[href]');
      if (!link || link.target === '_blank' || link.hasAttribute('download')) return;

      try {
        const url = new URL(link.href, window.location.origin);
        if (url.origin !== window.location.origin || !url.pathname.includes('/products/')) return;
      } catch (_) {
        return;
      }

      this.markScrollRestoreIntent();
      this.persistSnapshot();
    }

    clearScrollSnapshot() {
      const key = this.snapshotStorageKey();
      this.snapshot = null;
      if (!key) return;

      try {
        window.sessionStorage.removeItem(key);
        window.sessionStorage.removeItem(this.snapshotRestoreIntentKey());
      } catch (_) {
        // Session storage is optional; there is nothing else to clear.
      }
    }

    replaceClientPage(slots, page, startIndex = 0) {
      return this.replaceClientPages([{ slots, page, startIndex }], page);
    }

    replaceClientPages(pages, currentPage) {
      if (!this.clientControlled || !Array.isArray(pages) || !pages.length) return false;

      this.observer?.disconnect();
      this.records.clear();
      const slots = [];
      pages.forEach(({ slots: pageSlots, page, startIndex = 0 }) => {
        if (!pageSlots?.length || !Number.isInteger(page) || page < 1) return;

        pageSlots.forEach((slot, index) => {
          slot.dataset.rfProductIndex = String(startIndex + index + 1);
          this.markSlot(slot, page, index + 1);
        });
        this.records.set(
          page,
          this.createRecord({
            page,
            url: this.currentDocumentUrl(),
            previousUrl: null,
            nextUrl: null,
            slots: pageSlots,
          })
        );
        slots.push(...pageSlots);
      });
      if (!slots.length || !this.records.has(currentPage)) return false;

      this.grid.replaceChildren(...slots);
      this.currentPage = currentPage;
      this.dataset.currentPage = String(currentPage);
      this.lastDirection = 'forward';
      this.enforceMediaBudget();
      this.observeAllRecords();
      this.scheduleSync();
      return true;
    }

    appendClientPage(slots, page, startIndex = 0) {
      if (!this.clientControlled || !slots?.length || this.records.has(page)) return;

      slots.forEach((slot, index) => {
        slot.dataset.rfProductIndex = String(startIndex + index + 1);
        this.markSlot(slot, page, index + 1);
      });
      this.makeRoomForMedia(slots, 'forward');
      const record = this.createRecord({
        page,
        url: this.currentDocumentUrl(),
        previousUrl: null,
        nextUrl: null,
        slots,
      });

      this.insertRecord(record);
      this.records.set(page, record);
      this.enforceMediaBudget();
      this.observeAllRecords();
      this.scheduleSync();
    }

    removeClientSlot(slot) {
      if (!this.clientControlled || !slot) return false;

      const page = Number.parseInt(slot.dataset.rfPage, 10);
      const record = this.records.get(page);
      if (record) {
        const index = record.slots.indexOf(slot);
        if (index !== -1) record.slots.splice(index, 1);
        if (!record.slots.length) this.records.delete(page);
      }

      this.observer?.unobserve(slot);
      slot.remove();
      this.enforceMediaBudget();
      this.observeAllRecords();
      this.scheduleSync();
      return true;
    }

    markSlot(slot, page, fallbackIndex) {
      slot.dataset.rfPage = String(page);
      if (!slot.dataset.rfProductIndex) slot.dataset.rfProductIndex = String(fallbackIndex);
      slot.classList.add('scroll-trigger--cancel');
    }

    onIntersection(entries) {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;

        const page = Number.parseInt(entry.target.dataset.rfPage, 10);
        const record = this.records.get(page);
        if (!record) continue;

        if (this.hasUnloadedImages(entry.target)) this.restoreSlotImages(entry.target);
      }
    }

    onScroll() {
      if (!this.restoringScroll) this.hasUserScrolled = true;
      this.updateBackToTopVisibility();
      this.scheduleSync();
    }

    updateBackToTopVisibility() {
      const currentScrollY = Math.max(0, window.scrollY);
      const scrollDelta = currentScrollY - this.lastScrollY;
      this.previousScrollY = this.lastScrollY;
      this.lastScrollY = currentScrollY;

      if (!this.backToTop) return;

      const gridTop = this.grid.getBoundingClientRect().top + currentScrollY;
      const hasMovedBeyondGridTop = currentScrollY > gridTop + 160;

      if (!hasMovedBeyondGridTop || scrollDelta > 0) {
        this.setBackToTopVisibility(false);
      } else if (scrollDelta < 0) {
        this.setBackToTopVisibility(true);
      }
    }

    setBackToTopVisibility(isVisible) {
      if (!this.backToTop || this.backToTopVisible === isVisible) return;

      this.backToTopVisible = isVisible;
      this.backToTop.classList.toggle('is-visible', isVisible);
      this.backToTop.setAttribute('aria-hidden', String(!isVisible));
      this.backToTop.tabIndex = isVisible ? 0 : -1;
    }

    onBackToTopClick(event) {
      event.preventDefault();
      this.cancelScrollRestoration();
      this.clearScrollSnapshot();

      const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
      const target = this.getBackToTopTarget();
      const headerHeight = Number.parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
      const top = Math.max(0, window.scrollY + target.getBoundingClientRect().top - headerHeight - 16);

      window.scrollTo({ top, behavior });
    }

    getBackToTopTarget() {
      const selector = this.dataset.backToTopTarget;
      if (!selector) return this.grid;

      try {
        return document.querySelector(selector) || this.grid;
      } catch {
        return this.grid;
      }
    }

    scheduleSync() {
      if (this.scrollFrame) return;
      this.scrollFrame = window.requestAnimationFrame(() => {
        this.scrollFrame = null;
        this.syncCurrentPage();
        this.loadBoundaryPage();
        this.scheduleSnapshot();
      });
    }

    syncCurrentPage() {
      const probe = Math.min(window.innerHeight * 0.35, 320);
      const visibleRecord = this.orderedRecords().find(
        (record) => record.slots.at(-1)?.getBoundingClientRect().bottom > probe
      );

      if (!visibleRecord || visibleRecord.page === this.currentPage) return;

      this.lastDirection = visibleRecord.page > this.currentPage ? 'forward' : 'backward';
      this.currentPage = visibleRecord.page;
      this.dataset.currentPage = String(visibleRecord.page);
      this.replaceCurrentHistory(visibleRecord);
      this.enforceMediaBudget();
    }

    loadBoundaryPage() {
      if (!this.hasUserScrolled || this.loading || this.restoringScroll) return;

      const records = this.orderedRecords();
      const last = records.at(-1);
      const movingForward = this.lastScrollY >= (this.previousScrollY || 0);

      if (
        movingForward
        && last?.slots.at(-1)?.getBoundingClientRect().bottom <= window.innerHeight + this.prefetchMargin
      ) {
        if (this.clientControlled && this.clientHasNext) {
          this.dispatchEvent(new CustomEvent('infinite-product-grid:load-next', {
            detail: { page: last.page, nextPage: last.page + 1 },
          }));
          return;
        }
        if (last.nextUrl) this.requestPage(last.nextUrl, 'forward');
      }
    }

    replaceCurrentHistory(record) {
      if (!record.url) return;

      const url = new URL(record.url, window.location.origin);
      const state = {
        ...(history.state || {}),
        searchParams: this.facetSearchParams(url),
        rfInfinitePage: record.page,
      };

      url.searchParams.delete(this.pageParam);
      url.searchParams.delete('section_id');
      history.replaceState(state, '', `${url.pathname}${url.search}${url.hash}`);
    }

    facetSearchParams(url) {
      const params = new URLSearchParams(url.search);
      params.delete(this.pageParam);
      params.delete('section_id');
      return params.toString();
    }

    requestPage(url, direction) {
      if (!url) return;

      if (this.loading) {
        this.queuedRequest = { url, direction };
        return;
      }

      if (this.records.has(this.pageFromUrl(url))) return;
      this.loadPage(url, direction);
    }

    async loadPage(url, direction) {
      this.loading = true;
      this.lastDirection = direction;
      this.setLoadingState(true);
      this.abortController = new AbortController();

      try {
        const requestUrl = new URL(url, window.location.origin);
        requestUrl.searchParams.set('section_id', this.sectionId);
        const response = await fetch(requestUrl, {
          signal: this.abortController.signal,
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });

        if (!response.ok) throw new Error(`Unable to load collection page (${response.status})`);

        const html = await response.text();
        if (!this.isConnected) return;

        this.mergePage(url, html);
        this.enforceMediaBudget();
        this.observeAllRecords();
        this.scheduleSync();
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Infinite collection loading failed.', error);
          this.pagination.hidden = false;
        }
      } finally {
        this.abortController = null;
        this.loading = false;
        this.setLoadingState(false);

        const queuedRequest = this.queuedRequest;
        this.queuedRequest = null;
        if (queuedRequest && this.isConnected) this.requestPage(queuedRequest.url, queuedRequest.direction);
      }
    }

    mergePage(requestUrl, html) {
      const { source, sourceSlots, page } = this.parsePageResponse(requestUrl, html);

      if (this.records.has(page)) return;

      const slots = this.importPageSlots(sourceSlots, page);

      this.makeRoomForMedia(slots, this.lastDirection);
      const record = this.createRecord({
        page,
        url: requestUrl,
        previousUrl: source.dataset.previousUrl,
        nextUrl: source.dataset.nextUrl,
        slots,
      });
      this.insertRecord(record);
      this.records.set(page, record);
    }

    parsePageResponse(requestUrl, html) {
      const responseDocument = new DOMParser().parseFromString(html, 'text/html');
      const source = responseDocument.querySelector('infinite-product-grid');
      const sourceGrid = source?.querySelector('#product-grid');
      const sourceSlots = sourceGrid
        ? Array.from(sourceGrid.children).filter((item) => item.matches('.grid__item'))
        : [];
      const page = Number.parseInt(source?.dataset.currentPage, 10) || this.pageFromUrl(requestUrl);

      if (!source || !sourceGrid || !sourceSlots.length || !page) {
        throw new Error('The collection response did not include a product grid.');
      }

      return { source, sourceSlots, page };
    }

    importPageSlots(sourceSlots, page) {
      return sourceSlots.map((slot, index) => {
        const importedSlot = document.importNode(slot, true);
        this.markSlot(importedSlot, page, index + 1);
        return importedSlot;
      });
    }

    async restoreServerPagesThrough(lastPage, currentPage, token) {
      if (
        this.clientControlled
        || this.loading
        || !Number.isInteger(lastPage)
        || lastPage < 1
        || !Number.isInteger(currentPage)
        || currentPage < 1
        || currentPage > lastPage
      ) return false;

      this.loading = true;
      this.setLoadingState(true);
      this.abortController = new AbortController();

      try {
        const records = [];
        if (this.currentPage === 1 && this.records.get(1)) {
          records.push(this.records.get(1));
        } else {
          records.push(await this.fetchServerRecord(1, this.abortController.signal));
        }

        const pagesToFetch = Array.from({ length: lastPage - 1 }, (_, index) => index + 2);
        const fetchedRecords = new Map();
        let nextFetchIndex = 0;
        const fetchWorker = async () => {
          while (nextFetchIndex < pagesToFetch.length) {
            if (token !== this.restoreToken) return false;

            const page = pagesToFetch[nextFetchIndex];
            nextFetchIndex += 1;
            const record = await this.fetchServerRecord(page, this.abortController.signal);
            if (token !== this.restoreToken) return false;
            fetchedRecords.set(page, record);
          }
          return true;
        };
        const parallelFetches = navigator.connection?.saveData ? 2 : 3;
        const completed = await Promise.all(
          Array.from({ length: Math.min(parallelFetches, pagesToFetch.length) }, fetchWorker)
        );
        if (completed.some((didComplete) => !didComplete)) return false;
        pagesToFetch.forEach((page) => records.push(fetchedRecords.get(page)));
        if (token !== this.restoreToken || !this.isConnected) return false;

        this.observer?.disconnect();
        this.records.clear();
        this.grid.replaceChildren(...records.flatMap((record) => record.slots));
        records.forEach((record) => this.records.set(record.page, record));
        this.currentPage = currentPage;
        this.dataset.currentPage = String(currentPage);
        this.lastDirection = 'forward';
        this.enforceMediaBudget();
        this.observeAllRecords();
        return true;
      } catch (error) {
        this.abortController?.abort();
        if (error.name !== 'AbortError') {
          console.error('Infinite collection restoration failed.', error);
          if (this.pagination) this.pagination.hidden = false;
        }
        return false;
      } finally {
        this.abortController = null;
        this.loading = false;
        this.setLoadingState(false);
      }
    }

    async fetchServerRecord(page, signal) {
      const requestUrl = new URL(window.location.href);
      requestUrl.searchParams.set(this.pageParam, String(page));
      requestUrl.searchParams.set('section_id', this.sectionId);
      const response = await fetch(requestUrl, {
        signal,
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });
      if (!response.ok) throw new Error(`Unable to restore collection page (${response.status})`);

      const { source, sourceSlots, page: responsePage } = this.parsePageResponse(requestUrl, await response.text());
      if (responsePage !== page) throw new Error('The collection restoration returned an unexpected page.');

      const slots = this.importPageSlots(sourceSlots, page);
      return this.createRecord({
        page,
        url: requestUrl,
        previousUrl: source.dataset.previousUrl,
        nextUrl: source.dataset.nextUrl,
        slots,
      });
    }

    insertRecord(record) {
      const insertionPoint = this.orderedRecords().find((candidate) => candidate.page > record.page)?.slots[0];
      const fragment = document.createDocumentFragment();
      record.slots.forEach((slot) => fragment.append(slot));

      if (insertionPoint) {
        this.grid.insertBefore(fragment, insertionPoint);
      } else {
        this.grid.append(fragment);
      }

      this.dispatchEvent(new CustomEvent('rfa:product-grid:items-added', {
        bubbles: true,
        detail: { slots: record.slots },
      }));
    }

    makeRoomForMedia(incomingSlots, direction) {
      const overflow = Math.max(0, this.liveMediaCount() + this.mediaCount(incomingSlots) - this.maxLiveMedia);
      if (!overflow) return;

      const remainingOverflow = overflow - this.unloadDistantImages(overflow);
      if (remainingOverflow > 0) this.unloadIncomingImages(incomingSlots, remainingOverflow, direction);
    }

    enforceMediaBudget() {
      const overflow = Math.max(0, this.liveMediaCount() - this.maxLiveMedia);
      if (overflow) this.unloadDistantImages(overflow);
    }

    unloadDistantImages(mediaOverflow) {
      let removedMedia = 0;
      const guardTop = -window.innerHeight;
      const guardBottom = window.innerHeight * 2;
      const candidates = this.orderedRecords()
        .flatMap((record) => record.slots)
        .filter((slot) => this.slotImages(slot).length && !slot.contains(document.activeElement))
        .map((slot) => {
          const rect = slot.getBoundingClientRect();
          const outsideGuard = rect.bottom < guardTop || rect.top > guardBottom;
          const outsideViewport = rect.bottom < 0 || rect.top > window.innerHeight;
          const distance = rect.bottom < 0 ? -rect.bottom : rect.top - window.innerHeight;
          return {
            slot,
            priority: outsideGuard ? 2 : outsideViewport ? 1 : 0,
            distance,
            media: this.mediaCount([slot]),
          };
        })
        .filter((candidate) => candidate.priority > 0)
        .sort((left, right) => {
          if (right.priority !== left.priority) return right.priority - left.priority;
          if (right.distance !== left.distance) return right.distance - left.distance;
          return this.lastDirection === 'forward'
            ? Number(left.slot.dataset.rfPage) - Number(right.slot.dataset.rfPage)
            : Number(right.slot.dataset.rfPage) - Number(left.slot.dataset.rfPage);
        });

      for (const candidate of candidates) {
        if (mediaOverflow <= 0) break;
        const removed = this.unloadSlotImages(candidate.slot);
        mediaOverflow -= removed;
        removedMedia += removed;
      }

      return removedMedia;
    }

    unloadIncomingImages(incomingSlots, mediaOverflow, direction) {
      const furthestSlots = direction === 'backward' ? incomingSlots : [...incomingSlots].reverse();

      for (const slot of furthestSlots) {
        if (mediaOverflow <= 0) break;
        mediaOverflow -= this.unloadSlotImages(slot);
      }
    }

    slotImages(slot) {
      return Array.from(slot.querySelectorAll('.rf-card__artwork > img.rf-art'));
    }

    unloadSlotImages(slot) {
      let removedImages = 0;

      slot.querySelectorAll('.rf-card__artwork').forEach((artwork) => {
        const images = Array.from(artwork.querySelectorAll(':scope > img.rf-art'));
        if (!images.length) return;

        artwork.dataset.rfImageMarkup = images.map((image) => image.outerHTML).join('');
        images.forEach((image) => image.remove());
        removedImages += images.length;
      });

      if (removedImages) {
        slot.dataset.rfImagesVirtual = 'true';
        slot.classList.add('rf-infinite-product-grid__image-placeholder');
      }
      return removedImages;
    }

    restoreSlotImages(slot) {
      slot.querySelectorAll('.rf-card__artwork[data-rf-image-markup]').forEach((artwork) => {
        const template = document.createElement('template');
        template.innerHTML = artwork.dataset.rfImageMarkup;
        artwork.append(template.content.cloneNode(true));
        delete artwork.dataset.rfImageMarkup;
      });

      delete slot.dataset.rfImagesVirtual;
      slot.classList.remove('rf-infinite-product-grid__image-placeholder');
      this.enforceMediaBudget();
      this.observeAllRecords();
    }

    hasUnloadedImages(slot) {
      return slot.dataset.rfImagesVirtual === 'true';
    }

    liveMediaCount() {
      return this.mediaCount(this.orderedRecords().flatMap((record) => record.slots));
    }

    mediaCount(slots) {
      return slots.reduce((count, slot) => count + this.slotImages(slot).length, 0);
    }

    orderedRecords() {
      return Array.from(this.records.values()).sort((left, right) => left.page - right.page);
    }

    visibleThroughPage() {
      return this.orderedRecords().reduce((lastVisiblePage, record) => {
        const isVisible = record.slots.some((slot) => {
          const bounds = slot.getBoundingClientRect();
          return bounds.top < window.innerHeight && bounds.bottom > 0;
        });
        return isVisible ? Math.max(lastVisiblePage, record.page) : lastVisiblePage;
      }, this.currentPage);
    }

    observeAllRecords() {
      this.observer?.disconnect();
      this.orderedRecords().forEach((record) => {
        const targets = new Set([
          record.slots[0],
          record.slots.at(-1),
          ...record.slots.filter((slot) => this.hasUnloadedImages(slot)),
        ]);
        targets.forEach((target) => {
          if (target) this.observer.observe(target);
        });
      });
    }

    setLoadingState(isLoading) {
      this.grid.setAttribute('aria-busy', String(isLoading));
      if (this.status) this.status.hidden = !isLoading;
      if (this.statusText) this.statusText.textContent = isLoading ? this.dataset.loadingLabel : '';
    }

    pageFromUrl(url) {
      const value = new URL(url, window.location.origin).searchParams.get(this.pageParam);
      return Number.parseInt(value, 10) || 1;
    }

    currentDocumentUrl() {
      return `${window.location.pathname}${window.location.search}${window.location.hash}`;
    }

    canonicalUrl(url) {
      const canonical = new URL(url, window.location.origin);
      canonical.searchParams.delete('section_id');
      return `${canonical.pathname}${canonical.search}${canonical.hash}`;
    }

    snapshotStorageKey() {
      try {
        const url = new URL(window.location.href);
        url.searchParams.delete(this.pageParam);
        url.searchParams.delete('section_id');
        return `rf-infinite-product-grid:v4:${this.sectionId}:${url.pathname}?${url.searchParams.toString()}`;
      } catch (_) {
        return null;
      }
    }

    snapshotRestoreIntentKey() {
      const key = this.snapshotStorageKey();
      return key ? `${key}:return` : null;
    }

    hasScrollRestoreIntent() {
      const key = this.snapshotRestoreIntentKey();
      if (!key) return false;

      try {
        return window.sessionStorage.getItem(key) === 'product';
      } catch (_) {
        return false;
      }
    }

    markScrollRestoreIntent() {
      const key = this.snapshotRestoreIntentKey();
      if (!key) return;

      try {
        window.sessionStorage.setItem(key, 'product');
      } catch (_) {
        // Scroll restoration is optional when storage is unavailable.
      }
    }

    readSnapshot() {
      const key = this.snapshotStorageKey();
      if (!key) return null;

      try {
        const snapshot = JSON.parse(window.sessionStorage.getItem(key) || 'null');
        if (
        snapshot?.version !== 4
          || !Number.isInteger(snapshot.page)
          || snapshot.page < 1
          || !Number.isInteger(snapshot.anchorIndex)
          || snapshot.anchorIndex < 0
          || !Number.isFinite(snapshot.anchorTop)
        ) {
          return null;
        }
        const renderThroughPage = snapshot.renderThroughPage;
        if (
          !Number.isInteger(renderThroughPage)
          || renderThroughPage < snapshot.page
        ) return null;

        return { ...snapshot, renderThroughPage };
      } catch (_) {
        return null;
      }
    }

    scheduleSnapshot() {
      if (!this.scrollPersistenceEnabled || this.persistTimer) return;
      this.persistTimer = window.setTimeout(() => {
        this.persistTimer = null;
        this.persistSnapshot();
      }, 160);
    }

    persistSnapshot() {
      if (!this.scrollPersistenceEnabled) return;

      if (!this.hasScrollRestoreIntent()) {
        this.clearScrollSnapshot();
        return;
      }

      const key = this.snapshotStorageKey();
      const record = this.records.get(this.currentPage);
      if (!key || !record?.slots.length) return;

      const probe = Math.min(window.innerHeight * 0.35, 320);
      const anchorIndex = Math.max(0, record.slots.findIndex(
        (slot) => slot.getBoundingClientRect().bottom > probe
      ));
      const anchor = record.slots[anchorIndex];
      if (!anchor) return;

      try {
        window.sessionStorage.setItem(key, JSON.stringify({
          version: 4,
          page: this.currentPage,
          renderThroughPage: this.visibleThroughPage(),
          anchorIndex,
          anchorTop: anchor.getBoundingClientRect().top,
        }));
      } catch (_) {
        // Scroll restoration is progressive enhancement; private browsing may disable storage.
      }
    }

    async restoreSavedSnapshot(snapshot) {
      if (!snapshot || this.clientControlled) return false;

      this.restoringScroll = true;
      const token = ++this.restoreToken;
      const restored = await this.restoreServerPagesThrough(
        Math.max(snapshot.page, snapshot.renderThroughPage || snapshot.page),
        snapshot.page,
        token
      );
      if (!restored || token !== this.restoreToken) {
        if (token === this.restoreToken) {
          this.restoringScroll = false;
          this.clearScrollSnapshot();
        }
        return false;
      }

      return this.restoreScrollPosition(snapshot);
    }

    restoreScrollPosition(snapshot) {
      if (!snapshot || snapshot.page !== this.currentPage) return false;

      const anchor = this.records.get(snapshot.page)?.slots[snapshot.anchorIndex];
      if (!anchor || !Number.isFinite(snapshot.anchorTop)) return false;

      if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
      return this.beginScrollRestoration(anchor, snapshot.anchorTop);
    }

    beginScrollRestoration(anchor, anchorTop) {
      this.cancelScrollRestoration();
      this.restoringScroll = true;
      const token = ++this.restoreToken;
      this.headerRestorationActive = true;
      window.dispatchEvent(new CustomEvent('rfa:scroll-restoration:start'));
      const completion = new Promise((resolve) => {
        this.resolveScrollRestoration = resolve;
      });
      const correctPosition = () => {
        if (token !== this.restoreToken || !this.isConnected || !anchor.isConnected) return false;

        const top = Math.max(0, window.scrollY + anchor.getBoundingClientRect().top - anchorTop);
        window.scrollTo({ top, behavior: 'auto' });
        this.previousScrollY = top;
        this.lastScrollY = top;
        this.scheduleSync();
        return true;
      };

      this.restoreFrame = window.requestAnimationFrame(() => {
        if (!correctPosition()) {
          this.finishScrollRestoration(token);
          return;
        }
        this.restoreFrame = window.requestAnimationFrame(() => {
          if (!correctPosition()) {
            this.finishScrollRestoration(token);
            return;
          }
          this.finishScrollRestoration(token);
        });
      });

      return completion;
    }

    finishScrollRestoration(token = this.restoreToken) {
      if (token !== this.restoreToken) return;

      if (this.restoreFrame) window.cancelAnimationFrame(this.restoreFrame);
      this.restoreFrame = null;
      this.restoringScroll = false;
      const resolve = this.resolveScrollRestoration;
      this.resolveScrollRestoration = null;
      resolve?.(true);
      if (this.headerRestorationActive) {
        this.headerRestorationActive = false;
        window.dispatchEvent(new CustomEvent('rfa:scroll-restoration:end'));
      }
      this.hasUserScrolled = true;
      this.scheduleSync();
    }

    cancelScrollRestoration() {
      if (!this.restoringScroll && !this.restoreFrame) return;

      this.restoreToken += 1;
      if (this.restoringScroll) this.abortController?.abort();
      if (this.restoreFrame) window.cancelAnimationFrame(this.restoreFrame);
      this.restoreFrame = null;
      this.restoringScroll = false;
      const resolve = this.resolveScrollRestoration;
      this.resolveScrollRestoration = null;
      resolve?.(false);
      if (this.headerRestorationActive) {
        this.headerRestorationActive = false;
        window.dispatchEvent(new CustomEvent('rfa:scroll-restoration:end'));
      }
    }

    onPageHide() {
      if (this.persistTimer) {
        window.clearTimeout(this.persistTimer);
        this.persistTimer = null;
      }
      this.persistSnapshot();
    }

    onVisibilityChange() {
      if (document.visibilityState !== 'hidden') return;

      if (this.persistTimer) {
        window.clearTimeout(this.persistTimer);
        this.persistTimer = null;
      }
      this.persistSnapshot();
    }
  }

  customElements.define('infinite-product-grid', InfiniteProductGrid);
}
